import { useState, useEffect, useCallback, useRef } from "react";
import { ErrorMessage, Formik, FormikConfig } from "formik";
import apiClient from "../../../application/apis/tenant-management/tenant-management-client";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { useAuthContextV2 } from "../../../auth-state";
import { reportAsyncError } from "../../../error-reporter";
import { CloudGovApiError } from "../../../application/domain/api-error";
import {
  isValidRedirect,
  parseQueryOfLocation,
} from "../../../utils/url-utils";
import { PrimaryButton, Spinner } from "@graffer-inc/console-design-kit";
import { LoginR } from "./route";
import { LoginTemplate } from "../../template/login-template";

type handleSubmitType = FormikConfig<{
  slug: string;
  accountName: string;
  password: string;
}>["onSubmit"];

export const LoginPage = () => {
  const { slug: initialSlug, to } = parseQueryOfLocation(useLocation()) as {
    slug: string;
    to?: string;
  };
  const history = useHistory();
  const authCtx = useAuthContextV2();
  const [errorMessage, setErrorMessage] = useState("");
  const [showExpiredPasswordError, setShowExpiredPasswordError] =
    useState(false);

  const handleSubmit = useCallback<handleSubmitType>(
    async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        setShowExpiredPasswordError(false);
        const { data: resp } = await apiClient.login(values);
        switch (resp["loginStep"]) {
          case "Complete": {
            await authCtx.syncAuth();
            const decoded = to ? decodeURIComponent(to) : null;
            if (decoded && isValidRedirect(decoded)) {
              // NOTE(adshidtadka): ローカルで別プロダクトにリダイレクトする際はポートを手動で変更しなければならない場合がある
              window.location.assign(decoded);
            } else {
              history.push("/");
            }
            break;
          }
          case "InputTOTP":
            sessionStorage.setItem("MFAJWT", resp["MFAJWT"]);
            sessionStorage.setItem("userId", resp["userId"]);
            history.push(`/login/mfa${location.search}`);
            break;
          default:
            reportAsyncError(
              new Error(`unexpected login step: ${resp["loginStep"]}`),
            );
            setErrorMessage("ログインに失敗しました。");
        }
      } catch (e) {
        if (CloudGovApiError.isBadRequest(e)) {
          setErrorMessage(
            "組織コード、アカウント、パスワードのいずれかが間違っています",
          );
          return;
        }
        if (CloudGovApiError.isUserJustLoginLocked(e)) {
          setErrorMessage(
            "このアカウントはロックされました。しばらく時間をおいてから再度ログインしていただくか、管理者にロックの解除を依頼してください。",
          );
          return;
        }
        if (CloudGovApiError.isUserLoginLocked(e)) {
          setErrorMessage(
            "このアカウントはロックされています。しばらく時間をおいてから再度ログインしていただくか、管理者にロックの解除を依頼してください。",
          );
          return;
        }
        if (
          CloudGovApiError.isErrorCode(
            e,
            "forbidden.not_permitted_network_access",
          )
        ) {
          setErrorMessage(
            "ご利用中のネットワークからのアクセスが許可されていません。",
          );
          return;
        }
        if (CloudGovApiError.tmpPasswordExpired(e)) {
          setShowExpiredPasswordError(true);
          return;
        }
        reportAsyncError(e);
      } finally {
        setSubmitting(false);
      }
    },
    [authCtx, history, to],
  );

  const slugInput = useRef<HTMLInputElement>(null);
  const accountNameInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const firstlyFocused =
      typeof initialSlug === "string" && initialSlug.length > 0
        ? accountNameInput
        : slugInput;
    firstlyFocused.current?.focus();
  }, [slugInput, accountNameInput, initialSlug]);

  if (authCtx.auth.isAuthed) {
    const decoded = to ? decodeURIComponent(to) : null;
    if (decoded && isValidRedirect(decoded)) {
      window.location.assign(decoded);
      return null;
    }
    return <Redirect to="/" />;
  }

  return (
    <LoginTemplate>
      <Formik
        initialValues={{
          slug: initialSlug ? initialSlug : "",
          accountName: "",
          password: "",
        }}
        validate={(values) => {
          const errors: any = {};
          if (!values.slug) {
            errors.slug = "組織コードは必須です。";
          }
          if (!values.accountName) {
            errors.accountName = "アカウント名は必須です。";
          }
          if (!values.password) {
            errors.password = "パスワードは必須です。";
          }
          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          /* and other goodies */
        }) => (
          <form onSubmit={handleSubmit}>
            {errorMessage !== "" && (
              <div
                className="notification is-danger is-light"
                style={{
                  marginTop: "12px",
                }}
              >
                {errorMessage}
              </div>
            )}
            {showExpiredPasswordError && (
              <div
                className="notification is-danger is-light"
                style={{
                  marginTop: "12px",
                }}
              >
                パスワードの有効期限が切れています。
                <br />
                <Link to={LoginR.RequestTmpResetPassword.link({})}>
                  仮パスワードの再設定
                </Link>
                を行うか、管理者にパスワードのリセットを依頼してください。
              </div>
            )}
            <div className="field">
              <label className="label" htmlFor="slug">
                組織コード
              </label>
              <div className="control">
                <input
                  data-for-rpa-id="slug"
                  name="slug"
                  className="input"
                  placeholder={"組織コード (例 city-graffer)"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.slug}
                  disabled={!!initialSlug && initialSlug.length > 0}
                  ref={slugInput}
                />
              </div>
              <ErrorMessage name="slug">
                {(msg: string) => <p className="help is-danger">{msg}</p>}
              </ErrorMessage>
            </div>
            <div className="field">
              <label className="label" htmlFor="accountName">
                アカウント名
              </label>
              <input
                name="accountName"
                className="input"
                data-for-rpa-id="account"
                placeholder={"アカウント名（英数字記号）"}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.accountName}
                ref={accountNameInput}
              />
              <ErrorMessage name="accountName">
                {(msg: string) => <p className="help is-danger">{msg}</p>}
              </ErrorMessage>
            </div>
            <div className="field">
              <label className="label" htmlFor="password">
                パスワード
              </label>
              <input
                type="password"
                name="password"
                className="input"
                data-for-rpa-id="password"
                placeholder={"パスワード"}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
              <ErrorMessage name="password">
                {(msg: string) => <p className="help is-danger">{msg}</p>}
              </ErrorMessage>
            </div>
            <div style={{ marginTop: "24px" }} className={"has-text-centered"}>
              <PrimaryButton
                text={"ログイン"}
                data-for-rpa-id="login"
                disabled={isSubmitting}
                type="submit"
              />
            </div>
            <Spinner loading={isSubmitting} />
          </form>
        )}
      </Formik>
      <div className="has-text-centered">
        <div className="is-divider" data-content="または" />
        <a className="c__link" href={`/login/sso${location.search}`}>
          シングルサインオンでログインする
        </a>
      </div>
    </LoginTemplate>
  );
};
